import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  FileText, 
  Plus, 
  Trash2, 
  Loader2,
  LogOut,
  Shield,
  Eye,
  EyeOff,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  LayoutDashboard,
  ClipboardList,
  Download,
  Calendar,
  Building2,
  Filter,
  Mail,
  Edit3,
  Save,
  X,
  Check
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import ValuationDetailPanel from '../components/admin/ValuationDetailPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboardPage = () => {
  const { user, token, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('valuations');
  const [users, setUsers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [topics, setTopics] = useState([]);
  const [valuations, setValuations] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // User form state
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'editor' });
  const [creatingUser, setCreatingUser] = useState(false);
  
  // Blog form state
  const [selectedTopic, setSelectedTopic] = useState('');
  const [customTitle, setCustomTitle] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Blog edit state
  const [editingArticle, setEditingArticle] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', content_html: '', meta_description: '', image_url: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Valuation filter state
  const [filterPurpose, setFilterPurpose] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingNewsletter, setIsExportingNewsletter] = useState(false);
  const [selectedValuation, setSelectedValuation] = useState(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!token) {
      navigate('/login');
      toast.error('Bitte melden Sie sich an');
    }
  }, [token, navigate]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      
      try {
        const headers = { 'Authorization': `Bearer ${token}` };
        
        // Fetch users (admin only)
        if (isAdmin()) {
          const usersRes = await fetch(`${API_URL}/api/auth/users`, { headers });
          if (usersRes.ok) {
            const usersData = await usersRes.json();
            setUsers(usersData.users || []);
          }
        }
        
        // Fetch articles, topics, purposes, subscribers
        const [articlesRes, topicsRes, purposesRes, subscribersRes] = await Promise.all([
          fetch(`${API_URL}/api/blog/articles?published_only=false`),
          fetch(`${API_URL}/api/blog/topics`),
          fetch(`${API_URL}/api/valuation/purposes`),
          fetch(`${API_URL}/api/newsletter/subscribers`)
        ]);
        
        if (articlesRes.ok) {
          const articlesData = await articlesRes.json();
          setArticles(articlesData.articles || []);
        }
        
        if (topicsRes.ok) {
          const topicsData = await topicsRes.json();
          setTopics(topicsData.topics || []);
        }
        
        if (purposesRes.ok) {
          const purposesData = await purposesRes.json();
          setPurposes(purposesData.purposes || []);
        }
        
        if (subscribersRes.ok) {
          const subscribersData = await subscribersRes.json();
          setSubscribers(subscribersData.subscribers || []);
        }
        
        // Fetch valuations
        await fetchValuations();
        
      } catch {
        toast.error('Fehler beim Laden der Daten');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, isAdmin]);

  // Fetch valuations with filters
  const fetchValuations = async () => {
    try {
      const params = new URLSearchParams();
      if (filterPurpose) params.set('purpose', filterPurpose);
      if (filterDateFrom) params.set('date_from', filterDateFrom);
      if (filterDateTo) params.set('date_to', filterDateTo);
      
      const res = await fetch(`${API_URL}/api/valuation/admin/list?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setValuations(data.valuations || []);
      }
    } catch {
      // Silent fail - data will remain empty
    }
  };

  // Apply filters
  const handleApplyFilters = () => {
    fetchValuations();
  };

  // Clear filters
  const handleClearFilters = () => {
    setFilterPurpose('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setTimeout(fetchValuations, 100);
  };

  // Export valuations to Excel
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams();
      if (filterPurpose) params.set('purpose', filterPurpose);
      if (filterDateFrom) params.set('date_from', filterDateFrom);
      if (filterDateTo) params.set('date_to', filterDateTo);
      
      const res = await fetch(`${API_URL}/api/valuation/admin/export?${params.toString()}`);
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bewertungen_export_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Export erfolgreich');
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      toast.error('Fehler beim Export');
    } finally {
      setIsExporting(false);
    }
  };

  // Export newsletter to Excel
  const handleExportNewsletter = async () => {
    setIsExportingNewsletter(true);
    try {
      const res = await fetch(`${API_URL}/api/newsletter/export`);
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `newsletter_export_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Export erfolgreich');
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      toast.error('Fehler beim Export');
    } finally {
      setIsExportingNewsletter(false);
    }
  };

  // Delete valuation
  const handleDeleteValuation = async (valuationId) => {
    if (!window.confirm('Bewertung wirklich löschen?')) return;

    try {
      const response = await fetch(`${API_URL}/api/valuation/admin/${valuationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error();

      setValuations(prev => prev.filter(v => v.id !== valuationId));
      toast.success('Bewertung gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Toggle follow-up status
  const handleToggleFollowup = async (valuationId, currentStatus) => {
    const newStatus = !currentStatus;
    
    try {
      const response = await fetch(`${API_URL}/api/valuation/admin/${valuationId}/followup?followup_done=${newStatus}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error();
      
      const data = await response.json();

      // Update local state
      setValuations(prev => prev.map(v => 
        v.id === valuationId 
          ? { ...v, followup_done: data.followup_done, followup_date: data.followup_date }
          : v
      ));
      
      toast.success(newStatus ? 'Follow-Up als erledigt markiert' : 'Follow-Up zurückgesetzt');
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  // Delete subscriber
  const handleDeleteSubscriber = async (subscriberId) => {
    if (!window.confirm('Abonnent wirklich löschen?')) return;

    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribers/${subscriberId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error();

      setSubscribers(prev => prev.filter(s => s.id !== subscriberId));
      toast.success('Abonnent gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Create user
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreatingUser(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newUser)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Fehler beim Erstellen');
      }

      const userData = await response.json();
      setUsers(prev => [...prev, userData]);
      setNewUser({ email: '', password: '', name: '', role: 'editor' });
      toast.success('Benutzer erstellt');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreatingUser(false);
    }
  };

  // Delete user
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Benutzer wirklich löschen?')) return;

    try {
      const response = await fetch(`${API_URL}/api/auth/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Fehler beim Löschen');
      }

      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('Benutzer gelöscht');
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Toggle user setting for receiving valuation copies
  const handleToggleValuationCopies = async (userId, currentValue) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/users/${userId}`, {
        method: 'PATCH',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ receive_valuation_copies: !currentValue })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Fehler beim Aktualisieren');
      }

      // Update local state
      setUsers(prev => prev.map(u => 
        u.id === userId 
          ? { ...u, receive_valuation_copies: !currentValue }
          : u
      ));
      toast.success(!currentValue 
        ? 'Bewertungskopien aktiviert' 
        : 'Bewertungskopien deaktiviert'
      );
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Generate blog
  const handleGenerate = async () => {
    if (!selectedTopic || !customTitle.trim()) {
      toast.error('Bitte Thema und Titel auswählen');
      return;
    }

    setIsGenerating(true);

    try {
      const response = await fetch(`${API_URL}/api/blog/generate`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          topic_id: selectedTopic,
          custom_title: customTitle
        })
      });

      if (!response.ok) throw new Error('Fehler bei der Generierung');

      const data = await response.json();
      setArticles(prev => [data.blog, ...prev]);
      toast.success('Blog-Artikel wurde erstellt!');
      setCustomTitle('');
    } catch (error) {
      toast.error('Fehler bei der Blog-Generierung');
    } finally {
      setIsGenerating(false);
    }
  };

  // Start editing article
  const handleEditArticle = (article) => {
    setEditingArticle(article.id);
    setImageError(false);
    setEditForm({
      title: article.title,
      content_html: article.content_html,
      meta_description: article.meta_description || '',
      image_url: article.image_url || ''
    });
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingArticle(null);
    setImageError(false);
    setEditForm({ title: '', content_html: '', meta_description: '', image_url: '' });
  };

  // Upload image for blog
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Ungültiger Dateityp. Erlaubt: JPEG, PNG, WebP, GIF');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Datei zu gross. Max 5MB erlaubt.');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/blog/upload-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload fehlgeschlagen');
      }

      const data = await response.json();
      setEditForm(prev => ({ ...prev, image_url: data.image_url }));
      toast.success('Bild erfolgreich hochgeladen');
    } catch (error) {
      toast.error(error.message || 'Fehler beim Hochladen');
    } finally {
      setIsUploading(false);
    }
  };

  // Save article changes
  const handleSaveArticle = async () => {
    if (!editingArticle) return;
    
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/blog/article/${editingArticle}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editForm)
      });

      if (!response.ok) throw new Error('Fehler beim Speichern');

      const data = await response.json();
      setArticles(prev => prev.map(a => a.id === editingArticle ? data.article : a));
      setEditingArticle(null);
      setEditForm({ title: '', content_html: '', meta_description: '', image_url: '' });
      toast.success('Artikel gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  // Publish/unpublish article
  const handlePublish = async (blogId, publish) => {
    try {
      const response = await fetch(`${API_URL}/api/blog/publish`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ blog_id: blogId, is_published: publish })
      });

      if (!response.ok) throw new Error();

      setArticles(prev => prev.map(a => 
        a.id === blogId ? { ...a, is_published: publish } : a
      ));
      
      toast.success(publish ? 'Artikel veröffentlicht' : 'Artikel zurückgezogen');
    } catch {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  // Delete article
  const handleDeleteArticle = async (blogId) => {
    if (!window.confirm('Artikel wirklich löschen?')) return;

    try {
      const response = await fetch(`${API_URL}/api/blog/article/${blogId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error();

      setArticles(prev => prev.filter(a => a.id !== blogId));
      toast.success('Artikel gelöscht');
    } catch {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Abgemeldet');
  };

  const selectedTopicData = topics.find(t => t.id === selectedTopic);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#FF0000]" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-[#1a1c1b] flex items-center gap-3">
                <LayoutDashboard className="text-[#FF0000]" />
                Admin-Dashboard
              </h1>
              <p className="text-[#5a5d5b] mt-1">
                Willkommen, {user?.name || user?.email}
              </p>
            </div>
            
            <Button
              onClick={handleLogout}
              variant="outline"
              className="text-[#5a5d5b] hover:text-[#FF0000] hover:border-[#FF0000]"
              data-testid="btn-logout"
            >
              <LogOut size={18} className="mr-2" />
              Abmelden
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-8 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('valuations')}
              className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors border-b-2 -mb-px ${
                activeTab === 'valuations'
                  ? 'text-[#FF0000] border-[#FF0000]'
                  : 'text-[#5a5d5b] border-transparent hover:text-[#1a1c1b]'
              }`}
              data-testid="tab-valuations"
            >
              <ClipboardList size={18} />
              Bewertungen
            </button>
            <button
              onClick={() => setActiveTab('newsletter')}
              className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors border-b-2 -mb-px ${
                activeTab === 'newsletter'
                  ? 'text-[#FF0000] border-[#FF0000]'
                  : 'text-[#5a5d5b] border-transparent hover:text-[#1a1c1b]'
              }`}
              data-testid="tab-newsletter"
            >
              <Mail size={18} />
              Newsletter
            </button>
            <button
              onClick={() => setActiveTab('blog')}
              className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors border-b-2 -mb-px ${
                activeTab === 'blog'
                  ? 'text-[#FF0000] border-[#FF0000]'
                  : 'text-[#5a5d5b] border-transparent hover:text-[#1a1c1b]'
              }`}
              data-testid="tab-blog"
            >
              <FileText size={18} />
              Blog
            </button>
            {isAdmin() && (
              <button
                onClick={() => setActiveTab('users')}
                className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors border-b-2 -mb-px ${
                  activeTab === 'users'
                    ? 'text-[#FF0000] border-[#FF0000]'
                    : 'text-[#5a5d5b] border-transparent hover:text-[#1a1c1b]'
                }`}
                data-testid="tab-users"
              >
                <Users size={18} />
                Benutzer
              </button>
            )}
          </div>

          {/* Valuations Tab */}
          {activeTab === 'valuations' && (
            <div className="space-y-6">
              {/* Filters */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-lg font-semibold text-[#1a1c1b] mb-4 flex items-center gap-2">
                  <Filter size={20} className="text-[#5a5d5b]" />
                  Filter
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="filter-purpose">Bewertungszweck</Label>
                    <Select value={filterPurpose || "all"} onValueChange={(v) => setFilterPurpose(v === "all" ? "" : v)}>
                      <SelectTrigger className="mt-1" data-testid="filter-purpose">
                        <SelectValue placeholder="Alle Zwecke" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle Zwecke</SelectItem>
                        {purposes.map(p => (
                          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="filter-date-from">Datum von</Label>
                    <Input
                      id="filter-date-from"
                      type="date"
                      value={filterDateFrom}
                      onChange={(e) => setFilterDateFrom(e.target.value)}
                      className="mt-1"
                      data-testid="filter-date-from"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="filter-date-to">Datum bis</Label>
                    <Input
                      id="filter-date-to"
                      type="date"
                      value={filterDateTo}
                      onChange={(e) => setFilterDateTo(e.target.value)}
                      className="mt-1"
                      data-testid="filter-date-to"
                    />
                  </div>
                  
                  <div className="flex items-end gap-2">
                    <Button
                      onClick={handleApplyFilters}
                      className="bg-[#FF0000] hover:bg-[#CC0000] text-white"
                      data-testid="btn-apply-filters"
                    >
                      Anwenden
                    </Button>
                    <Button
                      onClick={handleClearFilters}
                      variant="outline"
                      data-testid="btn-clear-filters"
                    >
                      Zurücksetzen
                    </Button>
                  </div>
                </div>
              </div>

              {/* Results Header */}
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-[#1a1c1b]">
                  {valuations.length} Bewertungen gefunden
                </h2>
                
                <Button
                  onClick={handleExport}
                  disabled={isExporting || valuations.length === 0}
                  className="bg-[#22C55E] hover:bg-[#16A34A] text-white"
                  data-testid="btn-export"
                >
                  {isExporting ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" />
                      Exportiere...
                    </>
                  ) : (
                    <>
                      <Download size={18} className="mr-2" />
                      Excel Export
                    </>
                  )}
                </Button>
              </div>

              {/* Valuations Table */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                {valuations.length === 0 ? (
                  <div className="py-16 text-center text-[#5a5d5b]">
                    <ClipboardList size={48} className="mx-auto mb-4 opacity-30" />
                    <p>Keine Bewertungen gefunden</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a5d5b] uppercase tracking-wider">Datum</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a5d5b] uppercase tracking-wider">Firma</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a5d5b] uppercase tracking-wider">Branche</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a5d5b] uppercase tracking-wider">Zweck</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a5d5b] uppercase tracking-wider">Bewertung</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a5d5b] uppercase tracking-wider">Kontakt</th>
                          <th className="px-6 py-4 text-center text-xs font-semibold text-[#5a5d5b] uppercase tracking-wider">Follow-Up</th>
                          <th className="px-6 py-4 text-right text-xs font-semibold text-[#5a5d5b] uppercase tracking-wider">Aktionen</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {valuations.map((v, idx) => (
                          <tr key={v.id || idx} className="hover:bg-gray-50 transition-colors" data-testid={`valuation-row-${idx}`}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-[#1a1c1b]">
                              <div className="flex items-center gap-2">
                                <Calendar size={14} className="text-[#888b89]" />
                                {v.created_at ? new Date(v.created_at).toLocaleDateString('de-CH') : '-'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <Building2 size={14} className="text-[#FF0000]" />
                                <span className="font-medium text-[#1a1c1b]">
                                  {v.company_name || v.basic_info?.company_name || '-'}
                                </span>
                              </div>
                              <div className="text-xs text-[#888b89] mt-0.5">
                                {v.request_data?.basic_info?.location || v.basic_info?.location || '-'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-[#5a5d5b]">
                              {v.industry || v.basic_info?.industry || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-2 py-1 bg-[#FFE5E5] text-[#CC0000] text-xs rounded-full">
                                {v.purpose || v.context?.purpose || '-'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-semibold text-[#1a1c1b]">
                                {v.value_low && v.value_high 
                                  ? `CHF ${Math.round(v.value_low).toLocaleString('de-CH')} - ${Math.round(v.value_high).toLocaleString('de-CH')}`
                                  : v.result_summary?.value_range || '-'}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-[#1a1c1b]">{v.contact_name || v.contact?.name || '-'}</div>
                              <div className="text-xs text-[#888b89]">{v.contact_email || v.contact?.email || '-'}</div>
                              <div className="text-xs text-[#888b89]">{v.request_data?.contact?.phone || v.contact?.phone || '-'}</div>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <div className="flex flex-col items-center gap-1">
                                <button
                                  onClick={() => handleToggleFollowup(v.id, v.followup_done)}
                                  className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                                    v.followup_done 
                                      ? 'bg-green-500 border-green-500 text-white' 
                                      : 'border-gray-300 hover:border-green-400'
                                  }`}
                                  title={v.followup_done ? 'Follow-Up erledigt' : 'Als erledigt markieren'}
                                  aria-label="Follow-Up Status"
                                  data-testid={`followup-checkbox-${idx}`}
                                >
                                  {v.followup_done && <Check size={14} />}
                                </button>
                                {v.followup_done && v.followup_date && (
                                  <span className="text-xs text-green-600">
                                    {new Date(v.followup_date).toLocaleDateString('de-CH')}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => setSelectedValuation(v)}
                                  className="p-2 text-[#5a5d5b] hover:text-[#FF0000] transition-colors"
                                  title="Details anzeigen"
                                  aria-label="Details anzeigen"
                                  data-testid={`btn-detail-${idx}`}
                                >
                                  <Eye size={18} />
                                </button>
                                <button
                                  onClick={() => handleDeleteValuation(v.id)}
                                  className="p-2 text-[#5a5d5b] hover:text-red-600 transition-colors"
                                  title="Löschen"
                                  aria-label="Bewertung löschen"
                                >
                                  <Trash2 size={18} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Valuation Detail Panel */}
              {selectedValuation && (
                <ValuationDetailPanel
                  valuation={selectedValuation}
                  onClose={() => setSelectedValuation(null)}
                />
              )}
            </div>
          )}

          {/* Newsletter Tab */}
          {activeTab === 'newsletter' && (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-[#1a1c1b]">
                  {subscribers.filter(s => s.is_active).length} Newsletter-Abonnenten
                </h2>
                
                <Button
                  onClick={handleExportNewsletter}
                  disabled={isExportingNewsletter || subscribers.length === 0}
                  className="bg-[#22C55E] hover:bg-[#16A34A] text-white"
                  data-testid="btn-export-newsletter"
                >
                  {isExportingNewsletter ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" />
                      Exportiere...
                    </>
                  ) : (
                    <>
                      <Download size={18} className="mr-2" />
                      Excel Export
                    </>
                  )}
                </Button>
              </div>

              {/* Subscribers Table */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                {subscribers.length === 0 ? (
                  <div className="py-16 text-center text-[#5a5d5b]">
                    <Mail size={48} className="mx-auto mb-4 opacity-30" />
                    <p>Keine Newsletter-Abonnenten</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a5d5b] uppercase tracking-wider">Angemeldet am</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a5d5b] uppercase tracking-wider">Vorname</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a5d5b] uppercase tracking-wider">Name</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a5d5b] uppercase tracking-wider">E-Mail</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a5d5b] uppercase tracking-wider">Status</th>
                          <th className="px-6 py-4 text-right text-xs font-semibold text-[#5a5d5b] uppercase tracking-wider">Aktionen</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {subscribers.map((s, idx) => (
                          <tr key={s.email || s.id || idx} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-[#1a1c1b]">
                              {s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString('de-CH') : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-[#1a1c1b]">
                              {s.first_name || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-[#1a1c1b]">
                              {s.last_name || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-[#5a5d5b]">
                              {s.email || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                s.is_active 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-gray-100 text-gray-500'
                              }`}>
                                {s.is_active ? 'Aktiv' : 'Inaktiv'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <button
                                onClick={() => handleDeleteSubscriber(s.id)}
                                className="p-2 text-[#5a5d5b] hover:text-red-600 transition-colors"
                                title="Löschen"
                              >
                                <Trash2 size={18} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Blog Tab */}
          {activeTab === 'blog' && (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Blog Generator */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
                  <h2 className="text-lg font-semibold text-[#1a1c1b] mb-6 flex items-center gap-2">
                    <Sparkles className="text-[#FF0000]" size={20} />
                    Neuen Artikel erstellen
                  </h2>

                  <div className="space-y-5">
                    <div>
                      <Label htmlFor="topic">SEO-Thema wählen *</Label>
                      <Select value={selectedTopic} onValueChange={setSelectedTopic}>
                        <SelectTrigger className="mt-1 bg-white" data-testid="select-blog-topic">
                          <SelectValue placeholder="Thema auswählen..." />
                        </SelectTrigger>
                        <SelectContent>
                          {topics.map(topic => (
                            <SelectItem key={topic.id} value={topic.id}>
                              {topic.id === 'eigenes-thema' ? '✨ ' + topic.title : topic.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {selectedTopicData && selectedTopicData.id !== 'eigenes-thema' && (
                        <div className="mt-3 p-3 bg-[#f8f9fa] rounded-lg">
                          <p className="text-sm text-[#5a5d5b] mb-2">{selectedTopicData.description}</p>
                          <div className="flex flex-wrap gap-1">
                            {selectedTopicData.keywords.map((kw) => (
                              <span key={kw} className="text-xs px-2 py-0.5 bg-[#FFE5E5] text-[#CC0000] rounded">
                                {kw}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {selectedTopicData && selectedTopicData.id === 'eigenes-thema' && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm text-blue-700">
                            <strong>Eigenes Thema:</strong> Geben Sie unten Ihre Artikelidee ein. 
                            Die KI wird automatisch einen SEO-optimierten Titel mit H1-H3 Struktur erstellen.
                          </p>
                        </div>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="blog-title">
                        {selectedTopic === 'eigenes-thema' ? 'Artikelidee / Thema *' : 'Artikel-Titel *'}
                      </Label>
                      <Input
                        id="blog-title"
                        value={customTitle}
                        onChange={(e) => setCustomTitle(e.target.value)}
                        placeholder={selectedTopic === 'eigenes-thema' 
                          ? "z.B. Tipps für den Firmenverkauf in Zürich" 
                          : "z.B. Unternehmensbewertung 2025"}
                        className="mt-1 bg-white"
                        data-testid="input-blog-title"
                      />
                      {selectedTopic === 'eigenes-thema' && (
                        <p className="text-xs text-[#888b89] mt-1">
                          Die KI optimiert Ihren Titel automatisch für SEO
                        </p>
                      )}
                    </div>

                    <Button
                      onClick={handleGenerate}
                      disabled={isGenerating || !selectedTopic || !customTitle.trim()}
                      className="w-full bg-[#FF0000] hover:bg-[#CC0000] text-white"
                      data-testid="btn-generate-blog"
                    >
                      {isGenerating ? (
                        <>
                          <Loader2 size={18} className="mr-2 animate-spin" />
                          Wird generiert...
                        </>
                      ) : (
                        <>
                          <Plus size={18} className="mr-2" />
                          Artikel generieren
                        </>
                      )}
                    </Button>

                    {isGenerating && (
                      <p className="text-sm text-center text-[#5a5d5b]">
                        Die KI erstellt Ihren Artikel...
                        <br />
                        {selectedTopic === 'eigenes-thema' 
                          ? 'SEO-Titel wird optimiert und Artikel generiert...'
                          : 'Dies kann bis zu 30 Sekunden dauern.'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Articles List */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h2 className="text-lg font-semibold text-[#1a1c1b] mb-6 flex items-center gap-2">
                    <FileText className="text-[#5a5d5b]" size={20} />
                    Alle Artikel ({articles.length})
                  </h2>

                  {articles.length === 0 ? (
                    <div className="py-12 text-center text-[#5a5d5b]">
                      <FileText size={48} className="mx-auto mb-4 opacity-30" />
                      <p>Noch keine Artikel erstellt.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {articles.map(article => (
                        <div 
                          key={article.id}
                          className={`p-4 rounded-xl border transition-colors ${
                            editingArticle === article.id
                              ? 'border-[#FF0000] bg-[#FFF5F5]'
                              : article.is_published 
                                ? 'border-green-200 bg-green-50/30' 
                                : 'border-gray-200 bg-gray-50/30'
                          }`}
                          data-testid={`article-row-${article.id}`}
                        >
                          {editingArticle === article.id ? (
                            /* Edit Mode */
                            <div className="space-y-4">
                              <div>
                                <Label>Titel</Label>
                                <Input
                                  value={editForm.title}
                                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label>Meta-Beschreibung</Label>
                                <Input
                                  value={editForm.meta_description}
                                  onChange={(e) => setEditForm({ ...editForm, meta_description: e.target.value })}
                                  className="mt-1"
                                />
                              </div>
                              
                              {/* Image Upload Section */}
                              <div>
                                <Label>Artikelbild</Label>
                                <div className="mt-2 space-y-3">
                                  {editForm.image_url && !imageError && (
                                    <div className="relative w-full max-w-md">
                                      <img 
                                        src={editForm.image_url} 
                                        alt="Vorschau" 
                                        className="w-full h-40 object-cover rounded-lg border"
                                        onError={() => setImageError(true)}
                                      />
                                      <button
                                        onClick={() => { setEditForm({ ...editForm, image_url: '' }); setImageError(false); }}
                                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                                        type="button"
                                        aria-label="Bild entfernen"
                                      >
                                        <X size={14} />
                                      </button>
                                    </div>
                                  )}
                                  
                                  {editForm.image_url && imageError && (
                                    <div className="relative w-full max-w-md">
                                      <div className="w-full h-40 flex flex-col items-center justify-center bg-red-50 rounded-lg border border-red-200 text-red-500 text-sm p-4 text-center">
                                        <p className="font-medium">Bild nicht gefunden</p>
                                        <p className="text-xs mt-1 text-red-400 break-all max-w-full overflow-hidden">{editForm.image_url}</p>
                                        <p className="text-xs mt-2 text-gray-500">Nutzen Sie eine externe Bild-URL</p>
                                      </div>
                                      <button
                                        onClick={() => { setEditForm({ ...editForm, image_url: '' }); setImageError(false); }}
                                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                                        type="button"
                                        aria-label="Bild entfernen"
                                      >
                                        <X size={14} />
                                      </button>
                                    </div>
                                  )}
                                  
                                  {/* External URL Input */}
                                  <div className="space-y-2">
                                    <Label className="text-xs text-gray-500">Externe Bild-URL (empfohlen)</Label>
                                    <Input
                                      type="url"
                                      value={editForm.image_url || ''}
                                      onChange={(e) => { setEditForm({ ...editForm, image_url: e.target.value }); setImageError(false); }}
                                      placeholder="https://example.com/bild.jpg"
                                      className="text-sm"
                                    />
                                    <p className="text-xs text-gray-400">
                                      Tipp: Laden Sie Bilder auf Ihre Website oder einen Dienst wie <a href="https://imgur.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Imgur</a> hoch und fügen Sie die URL hier ein.
                                    </p>
                                  </div>
                                  
                                  <div className="flex items-center gap-3 pt-2 border-t">
                                    <span className="text-xs text-gray-400">Oder Datei hochladen:</span>
                                    <label className="cursor-pointer">
                                      <input
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp,image/gif"
                                        onChange={(e) => { setImageError(false); handleImageUpload(e); }}
                                        className="hidden"
                                        data-testid="blog-image-upload"
                                      />
                                      <span className={`inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium transition-colors ${
                                        isUploading 
                                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                          : 'bg-white text-[#5a5d5b] hover:border-[#FF0000] hover:text-[#FF0000]'
                                      }`}>
                                        {isUploading ? (
                                          <>
                                            <Loader2 size={16} className="animate-spin" />
                                            Wird hochgeladen...
                                          </>
                                        ) : (
                                          <>
                                            <Plus size={16} />
                                            Bild hochladen
                                          </>
                                        )}
                                      </span>
                                    </label>
                                    <span className="text-xs text-green-600">
                                      ✓ Bilder bleiben nach Deployments erhalten
                                    </span>
                                  </div>
                                </div>
                              </div>
                              
                              <div>
                                <Label>Inhalt (HTML)</Label>
                                <textarea
                                  value={editForm.content_html}
                                  onChange={(e) => setEditForm({ ...editForm, content_html: e.target.value })}
                                  className="mt-1 w-full h-64 p-3 border rounded-lg text-sm font-mono"
                                />
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  onClick={handleSaveArticle}
                                  disabled={isSaving || isUploading}
                                  className="bg-[#22C55E] hover:bg-[#16A34A] text-white"
                                >
                                  {isSaving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                                  <span className="ml-2">Speichern</span>
                                </Button>
                                <Button
                                  onClick={handleCancelEdit}
                                  variant="outline"
                                >
                                  <X size={18} />
                                  <span className="ml-2">Abbrechen</span>
                                </Button>
                              </div>
                            </div>
                          ) : (
                            /* View Mode */
                            <div className="flex items-start justify-between gap-4">
                              {/* Thumbnail */}
                              {article.image_url && (
                                <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 relative">
                                  <img 
                                    src={article.image_url} 
                                    alt="" 
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-xs">
                                    Bild fehlt
                                  </div>
                                </div>
                              )}
                              
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  {article.is_published ? (
                                    <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                                      <CheckCircle2 size={14} />
                                      Veröffentlicht
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-1 text-xs text-amber-600 font-medium">
                                      <AlertCircle size={14} />
                                      Entwurf
                                    </span>
                                  )}
                                  <span className="text-xs text-[#888b89]">
                                    {new Date(article.created_at).toLocaleDateString('de-CH')}
                                  </span>
                                </div>
                                
                                <h3 className="font-semibold text-[#1a1c1b] truncate">
                                  {article.title}
                                </h3>
                                
                                <p className="text-sm text-[#5a5d5b] mt-1 line-clamp-2">
                                  {article.meta_description}
                                </p>
                                
                                <div className="flex items-center gap-3 mt-2 text-xs text-[#888b89]">
                                  <span>{article.category}</span>
                                  <span>•</span>
                                  <span>{article.word_count} Wörter</span>
                                  {!article.image_url && (
                                    <>
                                      <span>•</span>
                                      <span className="text-amber-500">Kein Bild</span>
                                    </>
                                  )}
                                </div>
                              </div>

                              <div className="flex items-center gap-2 flex-shrink-0">
                                <button
                                  onClick={() => handleEditArticle(article)}
                                  className="p-2 text-[#5a5d5b] hover:text-[#FF0000] transition-colors"
                                  title="Bearbeiten"
                                >
                                  <Edit3 size={18} />
                                </button>
                                
                                {article.is_published && (
                                  <a
                                    href={`/blog/${article.slug}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 text-[#5a5d5b] hover:text-[#FF0000] transition-colors"
                                    title="Ansehen"
                                  >
                                    <ExternalLink size={18} />
                                  </a>
                                )}
                                
                                <button
                                  onClick={() => handlePublish(article.id, !article.is_published)}
                                  className={`p-2 transition-colors ${
                                    article.is_published 
                                      ? 'text-green-600 hover:text-amber-600' 
                                      : 'text-[#5a5d5b] hover:text-green-600'
                                  }`}
                                  title={article.is_published ? 'Zurückziehen' : 'Veröffentlichen'}
                                >
                                  {article.is_published ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                                
                                <button
                                  onClick={() => handleDeleteArticle(article.id)}
                                  className="p-2 text-[#5a5d5b] hover:text-red-600 transition-colors"
                                  title="Löschen"
                                >
                                  <Trash2 size={18} />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && isAdmin() && (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* User Form */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
                  <h2 className="text-lg font-semibold text-[#1a1c1b] mb-6 flex items-center gap-2">
                    <Shield className="text-[#FF0000]" size={20} />
                    Neuen Benutzer erstellen
                  </h2>

                  <form onSubmit={handleCreateUser} className="space-y-4">
                    <div>
                      <Label htmlFor="user-name">Name</Label>
                      <Input
                        id="user-name"
                        value={newUser.name}
                        onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                        placeholder="Max Mustermann"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="user-email">E-Mail *</Label>
                      <Input
                        id="user-email"
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        placeholder="benutzer@email.ch"
                        className="mt-1"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="user-password">Passwort *</Label>
                      <Input
                        id="user-password"
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                        placeholder="Min. 6 Zeichen"
                        className="mt-1"
                        required
                        minLength={6}
                      />
                    </div>

                    <div>
                      <Label htmlFor="user-role">Rolle *</Label>
                      <Select 
                        value={newUser.role} 
                        onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="editor">Editor</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Button
                      type="submit"
                      disabled={creatingUser}
                      className="w-full bg-[#FF0000] hover:bg-[#CC0000] text-white"
                    >
                      {creatingUser ? (
                        <>
                          <Loader2 size={18} className="mr-2 animate-spin" />
                          Wird erstellt...
                        </>
                      ) : (
                        <>
                          <Plus size={18} className="mr-2" />
                          Benutzer erstellen
                        </>
                      )}
                    </Button>
                  </form>
                </div>
              </div>

              {/* Users List */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h2 className="text-lg font-semibold text-[#1a1c1b] mb-6 flex items-center gap-2">
                    <Users className="text-[#5a5d5b]" size={20} />
                    Alle Benutzer ({users.length})
                  </h2>

                  {users.length === 0 ? (
                    <div className="py-12 text-center text-[#5a5d5b]">
                      <Users size={48} className="mx-auto mb-4 opacity-30" />
                      <p>Keine Benutzer vorhanden</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {users.map(u => (
                        <div 
                          key={u.id}
                          className={`p-4 rounded-xl border transition-colors ${
                            u.role === 'admin' 
                              ? 'border-[#FF0000]/20 bg-[#FF0000]/5' 
                              : 'border-gray-200 bg-gray-50/30'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                u.role === 'admin' ? 'bg-[#FF0000]' : 'bg-[#5a5d5b]'
                              }`}>
                                <span className="text-white font-semibold text-sm">
                                  {(u.name || u.email).charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <h3 className="font-semibold text-[#1a1c1b]">
                                  {u.name || 'Kein Name'}
                                </h3>
                                <p className="text-sm text-[#5a5d5b]">{u.email}</p>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-4">
                              {/* Bewertungskopien Toggle */}
                              <button
                                onClick={() => handleToggleValuationCopies(u.id, u.receive_valuation_copies)}
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                                  u.receive_valuation_copies
                                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                }`}
                                title={u.receive_valuation_copies 
                                  ? 'Erhält Kopien aller Kundenbewertungen' 
                                  : 'Erhält keine Bewertungskopien'}
                              >
                                <Mail size={14} />
                                <span className="hidden sm:inline">
                                  {u.receive_valuation_copies ? 'Kopien aktiv' : 'Keine Kopien'}
                                </span>
                              </button>
                              
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                u.role === 'admin' 
                                  ? 'bg-[#FF0000] text-white' 
                                  : 'bg-gray-200 text-[#5a5d5b]'
                              }`}>
                                {u.role === 'admin' ? 'Admin' : 'Editor'}
                              </span>
                              
                              {u.id !== user?.id && (
                                <button
                                  onClick={() => handleDeleteUser(u.id)}
                                  className="p-2 text-[#5a5d5b] hover:text-red-600 transition-colors"
                                  title="Löschen"
                                >
                                  <Trash2 size={18} />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AdminDashboardPage;
